import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Statistic, Spin, Select, Space, DatePicker } from 'antd';
import {
  DollarOutlined,
  StockOutlined,
  ThunderboltOutlined,
  CalendarOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import dayjs from 'dayjs';
import PageLayout from '../components/layout/PageLayout';
import api from '../libs/api';

const { RangePicker } = DatePicker;

// Fiscal quarter helpers (FY Jul-Jun)
function getFiscalQuarter(d) {
  const m = d.month();
  if (m >= 6 && m <= 8) return { q: 1, start: d.year(), startM: 6, endM: 8 };
  if (m >= 9 && m <= 11) return { q: 2, start: d.year(), startM: 9, endM: 11 };
  if (m >= 0 && m <= 2) return { q: 3, start: d.year(), startM: 0, endM: 2 };
  return { q: 4, start: d.year(), startM: 3, endM: 5 };
}

function getFiscalYearStart(d) {
  return d.month() >= 6
    ? dayjs().year(d.year()).month(6).startOf('month')
    : dayjs().year(d.year() - 1).month(6).startOf('month');
}

const now = dayjs();
const curQ = getFiscalQuarter(now);
const prevQDate = dayjs().month(curQ.startM).startOf('month').subtract(1, 'day');
const prevQ = getFiscalQuarter(prevQDate);
const fyStart = getFiscalYearStart(now);
const fyEnd = dayjs(fyStart).add(1, 'year').subtract(1, 'day').endOf('day');
const lastFyStart = fyStart.subtract(1, 'year');
const lastFyEnd = fyStart.subtract(1, 'day').endOf('day');

const DATE_PRESETS = [
  { label: 'This Month', value: [now.startOf('month'), now.endOf('month')] },
  { label: 'Last Month', value: [now.subtract(1, 'month').startOf('month'), now.subtract(1, 'month').endOf('month')] },
  { label: 'This Quarter', value: [dayjs().year(curQ.start).month(curQ.startM).startOf('month'), dayjs().year(curQ.start).month(curQ.endM).endOf('month')] },
  { label: 'Last Quarter', value: [dayjs().year(prevQ.start).month(prevQ.startM).startOf('month'), dayjs().year(prevQ.start).month(prevQ.endM).endOf('month')] },
  { label: 'Last 6 Months', value: [now.subtract(6, 'month').startOf('month'), now.subtract(1, 'month').endOf('month')] },
  { label: `This Year (FY${fyStart.year()}/${fyStart.year() + 1})`, value: [fyStart, fyEnd] },
  { label: `Last Year (FY${lastFyStart.year()}/${lastFyStart.year() + 1})`, value: [lastFyStart, lastFyEnd] },
  { label: 'All Time', value: [dayjs('2024-01-01'), now.endOf('month')] },
];

const DEFAULT_FCAS_MARKETS = [
  'RAISE1SEC', 'RAISE6SEC', 'RAISE60SEC', 'RAISE5MIN',
  'LOWER1SEC', 'LOWER6SEC', 'LOWER60SEC', 'LOWER5MIN',
];

const fmt = (v) => {
  if (v == null) return '-';
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${Number(v).toFixed(2)}`;
};

const priceFmt = (v) => (v != null ? `$${Number(v).toFixed(2)}/MWh` : '-');

export default function VppRevenue() {
  const [dateRange, setDateRange] = useState([
    now.subtract(1, 'month').startOf('month'),
    now.subtract(1, 'month').endOf('month'),
  ]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [duid, setDuid] = useState(null);
  const [regionId, setRegionId] = useState('NSW1');
  const [fcasMarkets, setFcasMarkets] = useState(DEFAULT_FCAS_MARKETS);

  const fetchData = useCallback(async (range) => {
    if (!range || !range[0] || !range[1]) return;
    setLoading(true);
    try {
      const params = {
        start: range[0].format('YYYY-MM-DD'),
        end: range[1].format('YYYY-MM-DD'),
      };
      if (duid) params.duid = duid;
      if (regionId) params.regionId = regionId;
      if (fcasMarkets && fcasMarkets.length) params.fcasMarket = fcasMarkets.join(',');
      const res = await api.get('/api/vpp/summary', { params });
      setData(res.data.data);
    } catch { /* handled by interceptor */ }
    setLoading(false);
  }, [duid, regionId, fcasMarkets]);

  useEffect(() => { fetchData(dateRange); }, [dateRange, fetchData]);

  const stats = data?.stats || {};
  const rrpCurve = data?.rrpCurve || [];
  const fcasCurve = data?.fcasCurve || [];
  const fcasMarketPrices = data?.fcasMarketPrices || [];
  const revenueByDuid = data?.revenueByDuid || [];
  const filterOptions = data?.filterOptions || {};
  const selectedMarkets = data?.selectedMarkets || [];

  return (
    <PageLayout>
      <div className="page-header">
        <h1>VPP Revenue &amp; Market</h1>
        <p>Virtual power plant FCAS revenue and NEM market pricing (SQL Server)</p>
      </div>

      {/* Toolbar: Date Range + Filters */}
      <Card size="small" style={{ marginBottom: 16 }} bodyStyle={{ padding: '12px 16px' }}>
        <Space wrap size="middle" align="center">
          <CalendarOutlined style={{ color: '#54A2CC' }} />
          <RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates)}
            format="DD MMM YYYY"
            size="small"
            allowClear={false}
            presets={DATE_PRESETS}
          />
          <Select
            placeholder="Region"
            allowClear
            style={{ width: 110 }}
            size="small"
            value={regionId}
            onChange={(v) => setRegionId(v || null)}
            options={(filterOptions.regions || []).map(r => ({ label: r, value: r }))}
          />
          <Select
            placeholder="DUID"
            allowClear
            showSearch
            style={{ width: 160 }}
            size="small"
            value={duid}
            onChange={(v) => setDuid(v || null)}
            options={(filterOptions.duids || []).map(d => ({ label: d, value: d }))}
          />
          <Select
            placeholder="FCAS Markets (default 8)"
            mode="multiple"
            allowClear
            style={{ minWidth: 220 }}
            size="small"
            maxTagCount={2}
            value={fcasMarkets}
            onChange={(v) => setFcasMarkets(v.length ? v : null)}
            options={(filterOptions.fcasMarkets || []).map(m => ({ label: m, value: m }))}
          />
        </Space>
      </Card>

      <Spin spinning={loading}>
        {/* KPI Score Cards */}
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total FCAS Revenue"
                value={stats.totalRevenue}
                formatter={(v) => fmt(v)}
                prefix={<DollarOutlined style={{ color: '#05A677' }} />}
                valueStyle={{ color: '#05A677' }}
              />
              <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                {stats.duidCount ? `${stats.duidCount} DUIDs` : ''}
              </div>
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card>
              <Statistic
                title="Avg Energy Price (RRP)"
                value={stats.avgRRP}
                precision={2}
                prefix={<StockOutlined style={{ color: '#54A2CC' }} />}
                suffix="/MWh"
              />
              <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                Hourly average across period
              </div>
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card>
              <Statistic
                title="FCAS $/MW (Period)"
                value={stats.totalPerMW}
                formatter={(v) => fmt(v)}
                prefix={<ThunderboltOutlined style={{ color: '#f5b759' }} />}
                valueStyle={{ color: '#f5b759' }}
              />
              <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                {selectedMarkets.length} markets, 1 MW enabled
              </div>
            </Card>
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Card>
              <Statistic
                title="Active DUIDs"
                value={stats.duidCount || 0}
                prefix={<TeamOutlined style={{ color: '#845EC2' }} />}
              />
              <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                Units with FCAS revenue
              </div>
            </Card>
          </Col>
        </Row>

        {/* Charts Row 1: RRP Curve + FCAS Curve (line charts side by side) */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} lg={12}>
            <Card title="Energy Price (RRP) — 5min Intervals" size="small">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={rrpCurve} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="label"
                    fontSize={10}
                    interval={23}
                    tick={{ fill: '#666' }}
                  />
                  <YAxis tickFormatter={(v) => `$${v}`} fontSize={11} />
                  <Tooltip formatter={(v) => priceFmt(v)} />
                  <Line type="monotone" dataKey="avgRRP" stroke="#54A2CC" strokeWidth={1.5} dot={false} name="Avg RRP" />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="FCAS Price (Sum of Markets) — 5min Intervals" size="small">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={fcasCurve} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="label"
                    fontSize={10}
                    interval={23}
                    tick={{ fill: '#666' }}
                  />
                  <YAxis tickFormatter={(v) => `$${v}`} fontSize={11} />
                  <Tooltip formatter={(v) => priceFmt(v)} />
                  <Line type="monotone" dataKey="avgFcasPrice" stroke="#f5b759" strokeWidth={1.5} dot={false} name="Avg FCAS Price" />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>

        {/* Charts Row 2: FCAS Market Unit Prices + Revenue by DUID (bar charts) */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} lg={12}>
            <Card title="FCAS Market Avg Prices ($/MWh)" size="small">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={fcasMarketPrices} margin={{ top: 5, right: 20, bottom: 60, left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="market" angle={-35} textAnchor="end" fontSize={10} interval={0} />
                  <YAxis tickFormatter={(v) => `$${v}`} fontSize={11} />
                  <Tooltip formatter={(v) => [`$${Number(v).toFixed(2)}/MWh`, 'Avg Price']} />
                  <Bar dataKey="avgPrice" fill="#f5b759" radius={[4, 4, 0, 0]} name="Avg Price" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="Revenue by DUID" size="small">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={revenueByDuid.slice(0, 20)}
                  margin={{ top: 5, right: 20, bottom: 60, left: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="duid" angle={-35} textAnchor="end" fontSize={10} interval={0} />
                  <YAxis tickFormatter={(v) => fmt(v)} fontSize={11} />
                  <Tooltip formatter={(v) => [fmt(v), 'Revenue']} />
                  <Bar dataKey="revenue" fill="#05A677" radius={[4, 4, 0, 0]} name="Revenue" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>
      </Spin>
    </PageLayout>
  );
}
